// ── 상황판 통계 계산 훅 (useMemo) ─────────────────────────────────────────────
import { useMemo } from 'react';
import {
  uniqueNames, getCurrentYearMonthKst,
  extractRegion, isScheduleLeaveRow, toMd,
} from './utils';
import { toDateString } from '../../../utils/date';
import { groupRecentContracts } from './recentContractGroups';

/**
 * snapshot 데이터에서 렌더링에 필요한 통계 값을 계산
 * @param {object} snapshot - useSituationBoard의 snapshot 상태
 * @returns {object} stats
 */
export function useSituationStats(snapshot) {
  return useMemo(() => {
    const {
      today, tomorrow, employees, externalUsers, salesPeople,
      onlineUsers, attendanceRows, tmCalls, leaveRows,
      promotionalOrders, promoWorkflowSteps, contracts,
      cardRevenue, settlementNet, promoProfit,
    } = snapshot;

    // 재직 인원
    const inServiceEmployees = (employees || []).filter((u) => u.is_active !== false && !u.resign_date).length;
    const inServiceExternal  = (externalUsers || []).filter((u) => u.is_active !== false && !u.resign_date).length;
    const inServiceSales     = (salesPeople || []).filter((u) => u.is_active !== false && !u.resign_date).length;

    // 출퇴근
    const todayRows     = (attendanceRows || []).filter((r) => toDateString(r.date || '') === today);
    const checkedIn     = todayRows.filter((r) => Boolean(r.check_in)).length;
    const checkedOut    = todayRows.filter((r) => Boolean(r.check_out)).length;
    const notCheckedOut = todayRows.filter((r) => Boolean(r.check_in) && !r.check_out).length;
    const checkedInNames = uniqueNames(
      todayRows.filter((r) => Boolean(r.check_in) && !r.check_out).map((r) => ({ name: r.name }))
    );

    // 콜 · 계약
    const monthCalls       = (tmCalls || []).length;
    const monthContracts   = (tmCalls || []).filter((r) => r.is_contracted === true || r.contract_status === 'normal').length;
    const pendingContracts = (tmCalls || []).filter((r) => r.contract_status === 'pending').length;
    const conversionRate   = monthCalls > 0 ? Math.round((monthContracts / monthCalls) * 100) : 0;

    // 내일 방문 일정 요약 (TM→마케터→지역 그룹)
    const tomorrowMeetings = (tmCalls || [])
      .filter((r) => {
        const sp = String(r.sales_person_name || '').trim();
        return toDateString(r.visit_date || '') === tomorrow && r.address && sp && sp !== '-';
      })
      .sort((a, b) => String(a.visit_time || '').localeCompare(String(b.visit_time || '')));
    const meetingGroupMap = {};
    tomorrowMeetings.forEach((r) => {
      const sender   = String(r.user_name || '-').trim();
      const marketer = String(r.sales_person_name || '-').trim();
      const region   = extractRegion(r.address);
      const category = String(r.ticket_type_category || r.ticket_type || '').trim() || '기타';
      const key = `${sender}__${marketer}__${region}__${category}`;
      if (!meetingGroupMap[key]) meetingGroupMap[key] = { sender, marketer, region, count: 0, category };
      meetingGroupMap[key].count += 1;
    });
    const meetingSummary = Object.values(meetingGroupMap).sort((a, b) => {
      if (a.sender !== b.sender)   return a.sender.localeCompare(b.sender, 'ko-KR');
      if (a.marketer !== b.marketer) return a.marketer.localeCompare(b.marketer, 'ko-KR');
      return a.region.localeCompare(b.region, 'ko-KR');
    });

    // 연차
    const leaveScheduleRows = (leaveRows || []).filter((r) => !r.is_correction && isScheduleLeaveRow(r));
    const todayLeaveNames   = uniqueNames(leaveScheduleRows.filter((r) => toDateString(r.date || '') === today));
    const upcomingLeaveEntries = leaveScheduleRows
      .filter((r) => toDateString(r.date || '') > today)
      .map((r) => ({ date: toDateString(r.date || ''), name: String(r.name || '').trim() }))
      .filter((r) => r.date && r.name)
      .sort((a, b) => a.date !== b.date ? a.date.localeCompare(b.date) : a.name.localeCompare(b.name, 'ko-KR'))
      .slice(0, 10)
      .map((r) => `${toMd(r.date)} ${r.name}`);

    // 홍보물 단계별
    const { year, month } = getCurrentYearMonthKst();
    const monthYM = `${year}-${String(month).padStart(2, '0')}`;
    const monthPromos = (promotionalOrders || []).filter((r) => String(r.received_date || '').slice(0, 7) === monthYM);
    const activeSteps = (promoWorkflowSteps || []).filter((s) => s.is_active).sort((a, b) => a.sort_order - b.sort_order);
    const promoByStep = [];
    const unassigned = monthPromos.filter((r) => !r.workflow_step_id);
    if (unassigned.length > 0) promoByStep.push({ stepId: null, stepName: '미처리', count: unassigned.length, orders: unassigned });
    activeSteps.forEach((step) => {
      const orders = monthPromos.filter((r) => Number(r.workflow_step_id) === step.id);
      if (orders.length > 0) promoByStep.push({ stepId: step.id, stepName: step.name, count: orders.length, orders });
    });

    // 홍보물 수익 — API(promoProfit)에서 원가 포함 순수익 데이터를 사용
    // API 실패 시 원가 정보가 없으므로 null (잘못된 수익 표시 방지)
    const computedPromoProfit = (promoProfit && Array.isArray(promoProfit.data) && promoProfit.data.length > 0)
      ? promoProfit
      : null;

    // 최근 계약 20건
    const recentContracts = (contracts || []).filter((r) => r.contract_status !== 'cancelled').slice(0, 20);

    // 모바일 상단 최근 계약: 오늘 + 가장 최근 과거 날짜 (계약 있는 날) 2개 표시 — recentContractGroups.js
    const mobileTopContracts = groupRecentContracts(contracts, today);

    // 마케터별/TM별 이번달 계약 집계
    const contractsByMarketer = {};
    (tmCalls || []).filter((r) => r.is_contracted === true || r.contract_status === 'normal').forEach((r) => {
      const mktName = String(r.sales_person_name || '미지정').trim();
      if (!contractsByMarketer[mktName]) contractsByMarketer[mktName] = {};
      /* contract_items 배열 기반으로 마케터별 상품 집계 — 서비스 수량 포함 (2026-09-01 사용자 확정:
         계약 200 + 서비스 20 = 220장) */
      const items = Array.isArray(r.contract_items) ? r.contract_items : [];
      items.forEach((i) => {
        const key = i.ticketTypeName || '기타';
        if (!contractsByMarketer[mktName][key]) contractsByMarketer[mktName][key] = 0;
        contractsByMarketer[mktName][key] += Number(i.quantity) || 0;
      });
    });
    const marketerContractList = Object.entries(contractsByMarketer)
      .map(([name, prod]) => ({ name, products: Object.entries(prod).map(([pname, cnt]) => ({ name: pname, count: cnt })), total: Object.values(prod).reduce((s, c) => s + c, 0) }))
      .sort((a, b) => b.total - a.total);

    const contractsByTm = {};
    (tmCalls || []).filter((r) => r.is_contracted === true || r.contract_status === 'normal').forEach((r) => {
      const tmName = String(r.user_name || '미지정').trim();
      if (!contractsByTm[tmName]) contractsByTm[tmName] = {};
      /* contract_items 배열 기반으로 TM(상담자)별 상품 집계 — 서비스 제외 유지 (2026-09-01 사용자 확정:
         서비스 포함은 마케터별만) */
      const items = Array.isArray(r.contract_items) ? r.contract_items : [];
      items.filter((i) => !i.isService).forEach((i) => {
        const key = i.ticketTypeName || '기타';
        if (!contractsByTm[tmName][key]) contractsByTm[tmName][key] = 0;
        contractsByTm[tmName][key] += Number(i.quantity) || 0;
      });
    });
    const tmContractList = Object.entries(contractsByTm)
      .map(([name, prod]) => ({ name, products: Object.entries(prod).map(([pname, cnt]) => ({ name: pname, count: cnt })), total: Object.values(prod).reduce((s, c) => s + c, 0) }))
      .sort((a, b) => b.total - a.total);

    // 오늘/내일 방문 지역 그룹 (지도 패널용)
    const buildMeetingGroups = (dateStr) => {
      const groupMap = {};
      (tmCalls || []).filter((r) => {
        const sp = String(r.sales_person_name || '').trim();
        return toDateString(r.visit_date || '') === dateStr && r.address && sp && sp !== '-';
      }).forEach((r) => {
        const name   = String(r.sales_person_name).trim();
        const region = extractRegion(r.address);
        if (region === '-') return;
        const key = `${name}__${region}`;
        if (!groupMap[key]) groupMap[key] = { name, region, count: 0 };
        groupMap[key].count += 1;
      });
      return Object.values(groupMap).sort((a, b) => {
        if (a.name !== b.name) return a.name.localeCompare(b.name, 'ko-KR');
        return a.region.localeCompare(b.region, 'ko-KR');
      });
    };
    const todayMeetingGroups    = buildMeetingGroups(today);
    const tomorrowMeetingGroups = buildMeetingGroups(tomorrow);

    return {
      today, tomorrow,
      inServiceEmployees, inServiceExternal, inServiceSales,
      onlineCount: (onlineUsers || []).length,
      checkedIn, checkedOut, notCheckedOut, checkedInNames,
      monthCalls, monthContracts, pendingContracts, conversionRate,
      tomorrowMeetingCount: tomorrowMeetings.length,
      meetingSummary,
      todayLeaveNames, upcomingLeaveEntries,
      recentOnline: (onlineUsers || []).slice(0, 6),
      monthPromoTotal: monthPromos.length,
      promoByStep,
      recentContracts, mobileTopContracts,
      todayMeetingGroups, tomorrowMeetingGroups,
      tmCalls,
      cardRevenue, settlementNet, promoProfit: computedPromoProfit,
      marketerContractList, tmContractList,
    };
  }, [snapshot]);
}
